import {
  LitElement,
  html,
  css,
  unsafeHTML,
  ref,
  createRef,
  paths,
  t,
  styles,
  classMap,
  range,
  map,
} from '@hh/global.js'

class HHSlider extends LitElement {
  static properties = {
    active: {
      reflect: true,
    },
    progress: {
      type: Boolean,
    },
    breakpoint: {
      type: Number,
    },
    _current: {
      state: true,
    },
    _total: {
      state: true,
    },
    _totalActive: {
      state: true,
    },
    hideScrollbar: {
      type: Boolean,
      reflect: true,
      attribute: 'hide-scrollbar',
    },
    _pages: {
      state: true,
    },
    buttons: {
      type: Boolean,
    },
    hideButtonsMobile: {
      type: Boolean,
    },
    miniCart: {
      type: Boolean,
      reflect: true,
      attribute: 'mini-cart',
    },
    gap: {
      type: Boolean,
    },
    hideCarouselButtons: {
      type: Boolean,
      reflect: true,
      attribute: 'hide-carousel-buttons',
    },
    dotStyle: {
      type: String,
      reflect: true,
      attribute: 'dot-style',
    },
    autoplayInterval: {
      type: Number,
      reflect: true,
      attribute: 'autoplay-interval',
    },
    _autoplayPaused: {
      state: true
    },
  }

  $container = createRef()
  $next = createRef()
  $prev = createRef()

  constructor() {
    super()

    this._total = 0
    this._totalActive = 0
    this._current = 1
    this._current = 0
    this.items = []
    this._pages = []
    this.containerWidth = 0
    this._totalWidth = 0
    this.dotStyle = 'line'
    this._autoplayTimer = null
    this._autoplayPaused = false

    this.updateUI = this.updateUI.bind(this)
    this.move = this.move.bind(this)
    this.handleResize = this.handleResize.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()

    this.items = Array.from(this.querySelectorAll('[slot="item"]'))
    this._total = this.items.length

    this.observer = new ResizeObserver((...args) => {
      this.activate(...args)
    })

    window.addEventListener(
      'variant:change',
      this.updateCurrentActiveImages.bind(this)
    )
    this.updateCurrentActiveImages()
    if (this.buttons) {
      window.addEventListener('resize', this.handleResize)
    }

    this.startAutoplay()

    setTimeout(() => {
      this.$container?.value?.addEventListener('scroll', this.updateUI, {
        passive: true,
      })
      if (this.breakpoint) {
        this.observer.observe(document.body)
      }
      this._containerWidth =
        this.closest('hh-slider').getBoundingClientRect().width
      this._totalWidth = this._total * this.items[0].clientWidth

      this._pages = Array(
        Math.ceil(
          (this._totalWidth - this._containerWidth) / this.items[0].clientWidth
        ) + 1
      ).fill(1)
      setTimeout(() => {
        const carouselButtons = this.shadowRoot.querySelectorAll('button, [href], input, select, textarea')

        this.dispatchEvent(new CustomEvent('carousel-buttons-updated', {
          detail: { carouselButtons },
          bubbles: true,
          composed: true
        }));
      }, 1000)
    }, 0)
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.$container?.value?.removeEventListener('scroll', this.updateUI)
    this.observer.disconnect()
    this.removeEventListener('variant:change', this.updateCurrentActiveImages)
    if (this._autoplayTimer) {
      clearInterval(this._autoplayTimer)
    }
  }

  firstUpdated() {
    this.$container?.value?.addEventListener('scroll', this.updateUI)
    if (this.breakpoint) {
      this.observer.observe(document.body)
    }

    this.updateCurrentActiveImages()
  }

  activate([first, ...all]) {
    const { contentRect: { width = 0 } = {} } = first || {}
    if (width < this.breakpoint && !this.active) {
      this.active = true
    } else if (width >= this.breakpoint && this.active) {
      this.active = false
    }
  }

  handleResize() {
    // if the container is wider than all the cards and all the gaps,
    // it doesn't need to be a slider anymore
    this._containerWidth =
      this.closest('hh-slider').getBoundingClientRect().width
    this._totalWidth = this._total * this.items[0].clientWidth
    this._pages = Array(
      Math.ceil(
        (this._totalWidth - this._containerWidth) / this.items[0].clientWidth
      ) + 1
    ).fill(1)
    this.active = this._containerWidth <= this._totalWidth
  }

  updateUI() {
    if (!this._total) {
      return
    }

    let items = this.items
    if (this.activeItems?.length > 0) {
      items = this.activeItems
    }

    if (
      this.$container?.value?.scrollLeft + this._containerWidth >
      this._totalWidth
    ) {
      this._current = this._pages.length - 1
    } else {
      this._current = Math.round(
        this.$container?.value?.scrollLeft / items[0].clientWidth
      )
    }
  }

  startAutoplay() {
    if (!this._autoplayPaused && this.autoplayInterval && this._total > 1) {
      this._autoplayTimer = setInterval(() => {
        const next = (this._current + 1) % this._total
        this.moveTo(next)
      }, this.autoplayInterval)
    }
  }

  stopAutoplay() {
    if (this._autoplayTimer) {
      clearInterval(this._autoplayTimer)
      this._autoplayTimer = null
    }
  }

  toggleAutoplay() {
    this._autoplayPaused = !this._autoplayPaused
    this.stopAutoplay()
    if (!this._autoplayPaused) {
      this.startAutoplay()
    }
  }

  updateCurrentActiveImages() {
    if (this.items.length === 0) return
    const activeImages = this.items.filter((item) => {
      return (
        item.classList.contains('is-active') ||
        item.classList.contains('is-active-additional') ||
        item.classList.contains('is-active-default')
      )
    })

    if (activeImages.length === 0) return
    this.activeItems = activeImages
    this._totalActive = activeImages.length
  }

  move(event) {
    event.preventDefault()

    let slideWidth = this.items[0].clientWidth
    if (slideWidth === 0) {
      slideWidth = this.activeItems[0].clientWidth
    }

    const slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.$container?.value?.scrollLeft + slideWidth
        : this.$container?.value?.scrollLeft - slideWidth
    this.$container?.value?.scrollTo({
      left: slideScrollPosition,
    })
  }

  moveTo(index) {
    let slideWidth = this.items[0].clientWidth
    if (slideWidth === 0) {
      slideWidth = this.activeItems[0].clientWidth
    }

    this.$container?.value?.scrollTo({
      left: slideWidth * index,
    })
  }

  onPaginationClick(evt, index) {
    evt.preventDefault()
    this.moveTo(index)
    this.stopAutoplay()
    this.startAutoplay()
  }

  render() {
    return html`
      <div
        class="${classMap({
      container: true,
      'container--gap': this.gap,
      'hide-scrollbar': this.hideScrollbar
    })}"
        ${ref(this.$container)}
        role="list"
        style="gap: ${this.gap}px"
      >
        <slot name="item"></slot>
      </div>
      <div class="${classMap({
      pagination: true,
      hide: this._total <= 1 || !this.active,
      'pagination--circle': this.dotStyle === 'circle',
    })}">
        ${map(range(this._totalActive), (idx) => {
      return html`
            <button
              @click="${(evt) => this.onPaginationClick(evt, idx)}"
              class="dot"
              aria-label="${t('accessibility.go_to_slide', 'Slide')} ${idx + 1}"
              aria-current="${this._current === idx ? 'true' : 'false'}"
              ?disabled="${this._current === idx}"
            >
              <span class="dot__inner"></span>
            </button>`
    })} 
        ${this.dotStyle === 'circle' && this.autoplayInterval ? html`
          <button @click="${this.toggleAutoplay}" class="autoplay-toggle" aria-label="Toggle autoplay">
            ${this._autoplayPaused
          ? html`
                  <svg viewBox="0 0 20 20" width="20" height="20">
                    <polygon points="2,1 2,19 18,10" />
                  </svg>
                `
          : html`
                  <svg viewBox="0 0 20 20" width="20" height="20">
                    <rect x="3" y="1" width="5" height="18" />
                    <rect x="12" y="1" width="5" height="18" />
                  </svg>
            `}
          </button>
        ` : ''}
        </div>
      </div>
      <div class="${classMap({
            buttons: true,
            'visually-hidden': !this.buttons || this._current === this._pages.length - 1 && this._current === 0,
          })}">
        <div class="${classMap({
            counter: true,
            'visually-hidden': true,
          })}">
          <span class="counter--current">${this._current}</span>
          <span aria-hidden="true"> / </span>
          <span class="visually-hidden">${t('accessibility.of', 'of')}</span>
          <span class="counter--total">${this._totalActive
        ? this._totalActive
        : this._total
          ? this._total
          : ''
      }</span>
        </div>
        <button
          ${ref(this.$prev)}
          @click="${this.move}"
          type="button"
          class="button button--prev"
          name="previous"
          aria-label="${t('accessibility.previous_slide', 'Previous')}"
          ?disabled="${this._current === 0}"
        >
          ${unsafeHTML(paths.arrow_left)}
        </button>
        <button
          ${ref(this.$next)}
          @click="${this.move}"
          type="button"
          class="button button--next"
          name="next"
          aria-label="${t('accessibility.next_slide', 'Next')}"
          ?disabled="${this._current === this._pages.length - 1}"
        >
          ${unsafeHTML(paths.arrow_right)}
        </button>
      </div>
      ${this._pages.length > 1 && this.progress
        ? html`
              <ul
                class="${classMap({
          'carousel-buttons': true,
          'hide-carousel': this.hideCarouselButtons,
        })}"
              >
                ${this._pages.map(
          (item, index) =>
            html`<li class="carousel-buttons__li">
                      <button
                        class="carousel-button ${index === this._current &&
              'carousel-button--current'}"
                        @click=${() => this.moveTo(index)}
                        aria-label="Move to slide ${index + 1}"
                      ></button>
                    </li>`
        )}
              </ul>
            `
        : ''
      }
    `
  }

  static styles = [
    styles.visuallyHidden,
    css`
      :host([active='true']) {
        position: relative;
      }

      :host {
        position: relative;
        width: 100%;
        display: block;
      }

      .container {
        width: var(--hh-container-width, 100%);
        display: var(--hh-container-flex);
        flex-wrap: var(--hh-container-flex-wrap);
        margin: var(--hh-container-margin);
        overflow: hidden;
      }

      .container.hide-scrollbar {
        scrollbar-width: none;
      }

      .container.hide-scrollbar::-webkit-scrollbar {
        display: none;
      }

      :host([active='true']) .container {
        display: flex;
        flex-wrap: nowrap;
        position: relative;
        overflow-x: auto;
        scroll-snap-type: x mandatory;
        scroll-behavior: smooth;
        -webkit-overflow-scrolling: touch;
      }

      .container--gap {
        gap: 20px;
      }

      :host([active='true']) ::slotted(*) {
        width: var(--hh-cell-width) !important;
      }

      .pagination {
        position: absolute;
        bottom: -0.7500rem;
        left: 0;
        display: flex;
        width: 100%;
        justify-content: space-between;
      }

      .dot {
        flex: 1 0 auto;
        padding: 0.6250rem 0.1563rem;
        z-index: 2;
        border: none;
        background: transparent;
        cursor: pointer;
      }

      .dot:focus-visible {
        outline: 0.1250rem solid var(--hh-color-focus);
      }

      .dot__inner {
        display: block;
        width: 100%;
        background: var(--hh-dot-background, #000);
        transition: opacity var(--hh-duration-default);
        opacity: 0.1;
        height: 0.1875rem;
      }

      .dot[aria-current='true'] .dot__inner {
        opacity: 1;
      }

      .buttons {
        display: none;
        align-items: center;
        position: absolute;
        gap: 10px;
        right: 0;
        bottom: var(--hh-buttons-bottom, calc(100% + 30px)) ;
      }

      .pagination--circle {
        bottom: 2.8125rem;
        justify-content: center;
        gap: 0.6250rem;
        align-items: center;
        width: fit-content;
        left: 50%;
        transform: translateX(-50%);
      }

      .pagination--circle .dot {
        padding: 0;
        flex: initial;
      }

      .pagination--circle .dot__inner {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        border: 1px solid black;
        background: white;
        opacity: .5;
      }

      .pagination--circle .dot[aria-current='true'] .dot__inner {
        opacity: 1;
      }

      :host([active='true']) .buttons {
        display: flex;
      }

      @media screen and (max-width: 991px) {
        :host([hideButtonsMobile]) .buttons {
          display: none;
        }

        :host(.cart-upsell__slider) .carousel-buttons {
          margin-right: 0.5000rem;
        }

        :host(.cart-upsell__slider[mini-cart]) .carousel-buttons {
          margin-right: 1.2500rem;
        }
      }

      @media screen and (min-width: 992px) {
        :host(.cart-upsell__slider) .buttons {
          right: 4.3750rem;
        }
      }

      :host([mini-cart='true']) .buttons {
        bottom: auto;
        top: -65px;
      }

      @media screen and (min-width: 992px) {
        :host([mini-cart='true']) .buttons {
          right: auto;
          left: calc(60vw - 200px);
        }

        :host([mini-cart='true']) .button svg {
          height: 0.5625rem;
        }
      }

      .counter {
        margin: 0 1.2500rem;
      }

      .button {
        width: var(--hh-button-width, 44px);
        height: var(--hh-button-height, 44px);
        cursor: pointer;
        color: black;
        border: none;
        background: var(--hh-color-carousel-button);
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .button svg {
        height: 0.5000rem;
      }

      .button:disabled {
        cursor: not-allowed;
        opacity: 0.5;
      }

      .carousel-buttons {
        display: flex;
        height: 2px;
        list-style-type: none;
        margin: 20px 0 0;
        padding: 0;
      }

      .autoplay-toggle {
        background-color: #000000;
        border: none;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 1.2500rem;
        height: 1.2500rem;
        cursor: pointer;
        margin-right: -1.8750rem;
        position: relative;
      }

      .autoplay-toggle svg {
        display: block;
        fill: #FFFFFF;
        position: absolute;
        z-index: 2;
        width: 8px;
      }

      .carousel-buttons.hide-carousel {
        display: none;
      }

      @media screen and (min-width: 750px) {
        :host:not([mini-cart='true']) .carousel-buttons {
          display: none;
        }

        :host([mini-cart='true']) .carousel-buttons {
          left: calc(100% - 160px);
          margin-top: 0;
          max-width: 78px;
          position: absolute;
          top: -46px;
        }

        :host([mini-cart='true']) .carousel-button {
          padding: 1px 6px;
        }
      }

      @media screen and (min-width: 990px) {
        :host([mini-cart='true']) .carousel-buttons {
          left: calc(100% - 300px);
        }
      }

      .carousel-buttons__li {
        display: block;
        width: 100%;
      }

      .carousel-button {
        appearance: none;
        background-color: var(--hh-color-neutral);
        border: none;
        margin: 0;
        padding: 0;
        cursor: pointer;
        display: block;
        width: 100%;
        height: 100%;
      }

      :host([mini-cart='true']) .carousel-button {
        padding-bottom: 0;
      }

      .carousel-button--current {
        background-color: var(--hh-color-night);
      }

      .hide {
        display: none;
      }
    `,
  ]
}

customElements.define('hh-slider', HHSlider)